/* Super Admin / Operations owner panels (Phase E/F):
   Insurance & Docs review, Corporate Approvals. All owner-only (backed by
   require_owner endpoints). Weekly payroll PDF reports live in PayrollReports. */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Clock, ShieldCheck, FileCheck2, DollarSign } from "lucide-react";
import { api } from "@/lib/auth";
import { Panel, CardLabel, StatusChip, fmtDate } from "@/components/portal/opsui";

const Empty = ({ icon: Icon, label, testid }) => (
  <div className="py-12 text-center" data-testid={testid}><Icon className="mx-auto h-7 w-7 text-muted-steel" />
    <p className="mt-3 text-sm text-ivory">{label}</p></div>
);
const Spin = () => <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gold-light" /></div>;

const DocRow = ({ d, onDecide, onDownload }) => (
  <div className="flex items-center justify-between rounded-xl border border-line bg-obsidian-900 p-3" data-testid={`doc-row-${d.document_id}`}>
    <div><p className="text-sm text-ivory">{d.original_filename} <span className="ml-1 text-[10px] uppercase tracking-wider text-muted-steel">{d.doc_type}</span></p>
      <p className="text-xs text-muted-steel">{d.company_name || d.scope} · {fmtDate(d.created_at)}</p></div>
    <div className="flex items-center gap-2">
      <StatusChip tone={d.status === "approved" ? "active" : d.status === "rejected" ? "canceled" : "review"} label={d.status.replace("_", " ")} />
      <button onClick={() => onDownload(d)} className="rounded-md border border-gold/30 px-2 py-1 text-[11px] text-gold-light hover:bg-gold/10">View</button>
      {d.status === "pending_review" && <>
        <button data-testid={`doc-approve-${d.document_id}`} onClick={() => onDecide(d, "approve")} className="rounded-md border border-gold/40 px-2 py-1 text-[11px] text-gold-light hover:bg-gold/10">Approve</button>
        <button data-testid={`doc-reject-${d.document_id}`} onClick={() => onDecide(d, "reject")} className="rounded-md border border-red-500/30 px-2 py-1 text-[11px] text-red-400 hover:bg-red-500/10">Reject</button></>}
    </div>
  </div>
);

export function DocumentsPanel() {
  const [docs, setDocs] = useState(null);
  const load = useCallback(() => { api.get("/ops/documents").then((r) => setDocs(r.data.documents)).catch(() => setDocs([])); }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (d, action) => { await api.post(`/ops/documents/${d.document_id}/${action}`, {}); load(); };
  const download = async (d) => {
    const r = await api.get(`/ops/documents/${d.document_id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 30000);
  };
  return (
    <div className="p-6" data-testid="ops-documents-section">
      <Panel>
        <CardLabel icon={ShieldCheck} right={<span className="text-[11px] text-dim-charcoal">Insurance & ID · Super Admin review</span>}>Documents</CardLabel>
        {docs === null ? <Spin /> : docs.length === 0 ? <Empty icon={FileCheck2} label="No documents uploaded yet" testid="ops-documents-empty" />
          : <div className="space-y-2">{docs.map((d) => <DocRow key={d.document_id} d={d} onDecide={decide} onDownload={download} />)}</div>}
      </Panel>
    </div>
  );
}

function ProposalList({ title, icon, listUrl, kind }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => { api.get(listUrl).then((r) => setItems(r.data.policies || r.data.proposals)).catch(() => setItems([])); }, [listUrl]);
  useEffect(() => { load(); }, [load]);
  const decide = async (p, action) => { const note = window.prompt(`Optional note for this ${action}`) || ""; await api.post(`/ops/${kind}/${p.proposal_id}/${action}`, { note }); load(); };
  return (
    <Panel>
      <CardLabel icon={icon}>{title}</CardLabel>
      {items === null ? <Spin /> : items.length === 0 ? <Empty icon={icon} label={`No ${title.toLowerCase()} yet`} testid={`ops-${kind}-empty`} />
        : <div className="space-y-2">{items.map((p) => (
          <div key={p.proposal_id} data-testid={`proposal-${p.proposal_id}`} className="flex items-center justify-between rounded-xl border border-line bg-obsidian-900 p-3">
            <div><p className="text-sm text-ivory">{p.name} <span className="text-[10px] text-muted-steel">v{p.version}</span></p>
              <p className="text-xs text-muted-steel">{p.company_name || p.company_id} · effective {p.effective_date}</p></div>
            <div className="flex items-center gap-2"><StatusChip tone={p.status === "approved" ? "active" : p.status === "rejected" ? "canceled" : p.status === "superseded" ? "review" : "upcoming"} label={p.status} />
              {p.status === "pending" && <>
                <button data-testid={`approve-${p.proposal_id}`} onClick={() => decide(p, "approve")} className="rounded-md border border-gold/40 px-2 py-1 text-[11px] text-gold-light hover:bg-gold/10">Approve</button>
                <button data-testid={`reject-${p.proposal_id}`} onClick={() => decide(p, "reject")} className="rounded-md border border-red-500/30 px-2 py-1 text-[11px] text-red-400 hover:bg-red-500/10">Reject</button></>}
            </div></div>))}</div>}
    </Panel>
  );
}

export function ApprovalsPanel() {
  return (
    <div className="space-y-5 p-6" data-testid="ops-approvals-section">
      <ProposalList title="Hour Policies" icon={Clock} listUrl="/ops/hour-policies" kind="hour-policies" />
      <ProposalList title="Pricing Proposals" icon={DollarSign} listUrl="/ops/pricing-proposals" kind="pricing-proposals" />
    </div>
  );
}
